import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  useCreateFolder, useCreateNote, useDeleteFolder, useDeleteNote, useNoteFolders,
  useNotes, useUpdateFolder, useUpdateNote, type NoteFolder, type PersonalNote,
} from '../api/hooks';
import { useLang, useT } from '../i18n/LangProvider';
import { toast } from '../ui/toast';
import { useConfirm } from '../ui/confirm';
import { Query } from '../ui/atoms';
import { Icon } from '../ui/Icon';
import { RichTextEditor } from '../ui/RichTextEditor';
import { useDialogFocus } from '../ui/useDialogFocus';

type TFn = (s: string, vars?: Record<string, string | number>) => string;

const COLORS = [
  { value: '#ffe9a8', label: 'Giallo' },
  { value: '#c8e6c9', label: 'Verde' },
  { value: '#bbdefb', label: 'Azzurro' },
  { value: '#f8bbd0', label: 'Rosa' },
  { value: '#e1bee7', label: 'Viola' },
  { value: '#ffccbc', label: 'Corallo' },
  { value: '#cfd8dc', label: 'Grigio' },
] as const;
type View = 'all' | 'none' | 'starred' | string; // string = folder id
type NoteDraft = {
  title: string;
  body: string;
  color: string | null;
  pinned: boolean;
  folderId: string | null;
};

const normaliseDraft = (draft: NoteDraft): NoteDraft => ({
  ...draft,
  title: draft.title.trim() || 'Senza titolo',
});

const sameDraft = (a: NoteDraft, b: NoteDraft) =>
  a.title === b.title
  && a.body === b.body
  && a.color === b.color
  && a.pinned === b.pinned
  && a.folderId === b.folderId;

function getNotePreview(body: string, t: TFn) {
  const doc = new DOMParser().parseFromString(body || '', 'text/html');
  const assets: { icon: 'table' | 'image' | 'video' | 'checklist'; label: string }[] = [];

  doc.querySelectorAll('table').forEach((table) => {
    const rows = table.querySelectorAll('tr').length;
    const cols = Math.max(0, ...Array.from(table.querySelectorAll('tr')).map((row) => row.querySelectorAll('th,td').length));
    const sample = Array.from(table.querySelectorAll('th,td'))
      .map((cell) => cell.textContent?.trim())
      .filter(Boolean)
      .slice(0, 3)
      .join(' · ');
    assets.push({ icon: 'table', label: t('Tabella {r}×{c}', { r: rows, c: cols }) });
    table.replaceWith(doc.createTextNode(sample ? `${sample}\n` : ''));
  });

  const images = doc.querySelectorAll('img').length;
  const videos = doc.querySelectorAll('video,iframe').length;
  const checks = doc.querySelectorAll('li.task,.task-box').length;
  if (images) assets.push({ icon: 'image', label: t(images === 1 ? '1 immagine' : '{n} immagini', { n: images }) });
  if (videos) assets.push({ icon: 'video', label: t('{n} video', { n: videos }) });
  if (checks) assets.push({ icon: 'checklist', label: t('{n} attività', { n: checks }) });
  doc.querySelectorAll('img,video,iframe').forEach((element) => element.remove());
  doc.querySelectorAll('p,div,h1,h2,h3,li,blockquote,pre,hr').forEach((element) => element.append('\n'));

  const text = (doc.body.textContent ?? '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, 240);
  return { text, assets: assets.slice(0, 3) };
}

function NoteCard({ note, onOpen, onTogglePin, onReorderDrop }: {
  note: PersonalNote; onOpen: () => void; onTogglePin: () => void; onReorderDrop: (draggedId: string, side: 'before' | 'after') => void;
}) {
  const { lang, t } = useLang();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const preview = getNotePreview(note.body, t);
  const [side, setSide] = useState<'before' | 'after' | null>(null);
  const accentStyle = note.color
    ? ({ '--note-accent': note.color } as CSSProperties)
    : undefined;
  return (
    <article className={`note-card ${note.color ? 'has-color' : ''} ${side ? `is-reorder-${side}` : ''}`} draggable style={accentStyle}
      onDragStart={(e) => { e.dataTransfer.setData('text/note', note.id); e.dataTransfer.effectAllowed = 'move'; }}
      onDragOver={(e) => {
        const dragged = e.dataTransfer.types.includes('text/note');
        if (!dragged) return;
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        setSide((e.clientX - r.left) / r.width < 0.5 ? 'before' : 'after');
      }}
      onDragLeave={() => setSide(null)}
      onDrop={(e) => {
        const id = e.dataTransfer.getData('text/note');
        if (id && id !== note.id && side) { e.preventDefault(); e.stopPropagation(); onReorderDrop(id, side); }
        setSide(null);
      }}>
      <button className="note-card__open" onClick={onOpen} aria-label={t('Apri {title}', { title: note.title || t('nota senza titolo') })}>
        <h2 className="note-card__title">{t(note.title || 'Senza titolo')}</h2>
        <p className="note-card__body">{preview.text || <span className="muted">{t('Nota vuota')}</span>}</p>
        {preview.assets.length > 0 && (
          <span className="note-card__assets">
            {preview.assets.map((asset) => (
              <span key={`${asset.icon}-${asset.label}`}><Icon name={asset.icon} size={11} /> {asset.label}</span>
            ))}
          </span>
        )}
        <span className="note-card__meta">
          <Icon name="clock" size={11} />
          {t('Aggiornata {date}', { date: new Date(note.updatedAt).toLocaleDateString(loc, { day: 'numeric', month: 'short' }) })}
        </span>
      </button>
      <button
        className={`note-card__star ${note.pinned ? 'is-on' : ''}`}
        title={note.pinned ? t('Rimuovi dai preferiti') : t('Aggiungi ai preferiti')}
        aria-label={note.pinned ? t('Rimuovi dai preferiti') : t('Aggiungi ai preferiti')}
        aria-pressed={note.pinned}
        onClick={(e) => { e.stopPropagation(); onTogglePin(); }}>
        <Icon name="star" size={13} />
      </button>
    </article>
  );
}

/** Custom modal to name/rename a folder (replaces window.prompt). */
function FolderNameModal({ title, initial, onCancel, onConfirm }: {
  title: string; initial: string; onCancel: () => void; onConfirm: (name: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState(initial);
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal card folder-name-modal" style={{ maxWidth: 380, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <h3 className="h2" style={{ fontSize: 15, marginBottom: 12 }}>{title}</h3>
        <input className="input" autoFocus value={name} maxLength={80} placeholder={t('Nome cartella')}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onConfirm(name.trim()); if (e.key === 'Escape') onCancel(); }} />
        <div className="cluster" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn--sm" onClick={onCancel}>{t('Annulla')}</button>
          <button className="btn btn--sm btn--brand" disabled={!name.trim()} onClick={() => onConfirm(name.trim())}>{t('Salva')}</button>
        </div>
      </div>
    </div>
  );
}

function NoteEditor({ note, folders, onClose }: {
  note: PersonalNote; folders: NoteFolder[]; onClose: () => void;
}) {
  const { lang, t } = useLang();
  const confirm = useConfirm();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const dialogRef = useRef<HTMLDivElement>(null);
  const update = useUpdateNote();
  const remove = useDeleteNote();
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const [color, setColor] = useState(note.color);
  const [pinned, setPinned] = useState(note.pinned);
  const [folderId, setFolderId] = useState<string | null>(note.folderId ?? null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const retryTimer = useRef<number | null>(null);
  const saveLoop = useRef<Promise<void> | null>(null);
  const flushRequested = useRef(false);
  const mounted = useRef(true);
  const discardOnUnmount = useRef(false);
  const closingRef = useRef(false);
  const savedDraft = useRef(normaliseDraft({
    title: note.title,
    body: note.body,
    color: note.color,
    pinned: note.pinned,
    folderId: note.folderId ?? null,
  }));
  const latestDraft = useRef(savedDraft.current);
  latestDraft.current = normaliseDraft({ title, body, color, pinned, folderId });
  const dirty = !sameDraft(latestDraft.current, savedDraft.current);

  const accentStyle = color
    ? ({ '--note-accent': color } as CSSProperties)
    : undefined;

  // Full folder path label so nested folders read clearly in the dropdown.
  const folderPath = (f: NoteFolder): string => {
    const parts: string[] = [f.name];
    let p = f.parentId;
    let hops = 0;
    while (p && hops++ < 20) { const parent = folders.find((x) => x.id === p); if (!parent) break; parts.unshift(parent.name); p = parent.parentId; }
    return parts.join(' / ');
  };

  function scheduleRetry() {
    if (!mounted.current || retryTimer.current || discardOnUnmount.current) return;
    retryTimer.current = window.setTimeout(() => {
      retryTimer.current = null;
      void flushLatest().catch(() => undefined);
    }, 4000);
  }

  async function flushLatest() {
    flushRequested.current = true;
    if (saveLoop.current) return saveLoop.current;

    const loop = (async () => {
      if (mounted.current) {
        setSaving(true);
        setSaveError(null);
      }
      while (!discardOnUnmount.current && (flushRequested.current || !sameDraft(latestDraft.current, savedDraft.current))) {
        flushRequested.current = false;
        const snapshot = { ...latestDraft.current };
        if (sameDraft(snapshot, savedDraft.current)) continue;
        try {
          await update.mutateAsync({ id: note.id, ...snapshot });
          savedDraft.current = snapshot;
          if (mounted.current) {
            setSaveError(null);
            setSavedAt(new Date().toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' }));
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : t('errore');
          if (mounted.current) setSaveError(message);
          scheduleRetry();
          throw error;
        }
      }
    })();
    saveLoop.current = loop;
    try {
      await loop;
    } finally {
      if (saveLoop.current === loop) saveLoop.current = null;
      if (mounted.current) setSaving(false);
    }
  }

  useEffect(() => {
    if (!dirty) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      void flushLatest().catch(() => undefined);
    }, 700);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, color, pinned, folderId]);

  useEffect(() => {
    mounted.current = true;
    const hasPendingWork = () =>
      !sameDraft(latestDraft.current, savedDraft.current) || saveLoop.current !== null;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasPendingWork() || discardOnUnmount.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const onPageHide = () => {
      if (hasPendingWork() && !discardOnUnmount.current) {
        void flushLatest().catch(() => undefined);
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      mounted.current = false;
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      if (timer.current) window.clearTimeout(timer.current);
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
      if (hasPendingWork() && !discardOnUnmount.current) {
        void flushLatest().catch(() => undefined);
      }
    };
    // Autosave is intentionally bound to this editor instance; current drafts
    // and the mutation queue are read through refs during teardown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function requestClose() {
    if (closingRef.current) return;
    closingRef.current = true;
    if (timer.current) window.clearTimeout(timer.current);
    setClosing(true);
    try {
      await flushLatest();
      onClose();
    } catch (error) {
      toast.error(`${t('Salvataggio non riuscito')}: ${error instanceof Error ? error.message : t('errore')}`);
      closingRef.current = false;
      setClosing(false);
    }
  }

  async function deleteCurrentNote() {
    if (!(await confirm({ message: t('Eliminare questa nota?'), confirmLabel: t('Elimina'), danger: true }))) return;
    discardOnUnmount.current = true;
    if (timer.current) window.clearTimeout(timer.current);
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
    try {
      if (saveLoop.current) await saveLoop.current.catch(() => undefined);
      await remove.mutateAsync(note.id);
      onClose();
    } catch (error) {
      discardOnUnmount.current = false;
      toast.error(`${t('Eliminazione non riuscita')}: ${error instanceof Error ? error.message : t('errore')}`);
    }
  }

  useDialogFocus(dialogRef, true, () => { void requestClose(); });

  const saveStatus = closing
    ? t('Salvataggio finale…')
    : saving
      ? t('Salvataggio…')
      : saveError
        ? t('Salvataggio non riuscito · nuovo tentativo')
      : dirty
        ? t('Modifiche in attesa')
        : savedAt
          ? t('Salvato alle {time}', { time: savedAt })
          : t('Salvato · {time}', { time: new Date(note.updatedAt).toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' }) });

  return (
    <div className="modal-backdrop note-editor-backdrop">
      <div
        ref={dialogRef}
        className={`modal note-editor ${color ? 'has-color' : ''}`}
        style={accentStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="note-editor-title"
        tabIndex={-1}
      >
        <div className="note-editor__head">
          <span className="note-editor__mark"><Icon name="edit" size={17} /></span>
          <div className="note-editor__identity">
            <input
              id="note-editor-title"
              data-dialog-initial-focus
              className="note-editor__title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('Titolo della nota')}
              aria-label={t('Titolo della nota')}
            />
            <span className="note-editor__status" role="status" aria-live="polite">{saveStatus}</span>
          </div>
          <button
            className={`icon-btn note-editor__options-toggle ${optionsOpen ? 'is-on' : ''}`}
            title={t('Opzioni nota')}
            aria-label={t('Opzioni nota')}
            aria-expanded={optionsOpen}
            aria-controls="note-editor-options"
            onClick={() => setOptionsOpen((open) => !open)}
          >
            <Icon name="settings" size={15} />
          </button>
          <button
            className={`icon-btn ${pinned ? 'is-on' : ''}`}
            title={pinned ? t('Rimuovi dai preferiti') : t('Aggiungi ai preferiti')}
            aria-label={pinned ? t('Rimuovi dai preferiti') : t('Aggiungi ai preferiti')}
            aria-pressed={pinned}
            onClick={() => setPinned((p) => !p)}
          >
            <Icon name="star" size={15} />
          </button>
          <button className="icon-btn" title={t('Chiudi')} aria-label={t('Salva e chiudi')} disabled={closing} onClick={() => void requestClose()}><Icon name="x" size={15} /></button>
        </div>

        <div className="note-editor__scroll">
          <RichTextEditor value={body} onChange={setBody} minHeight={260} placeholder={t('Scrivi i tuoi appunti… (grassetto, corsivo, link, immagini)')} />
        </div>

        <div className="note-editor__foot">
          <div id="note-editor-options" className={`note-editor__options ${optionsOpen ? 'is-open' : ''}`}>
            <div className="note-editor__option">
              <span className="note-editor__option-label">{t('Colore')}</span>
              <div className="note-swatches">
                <button className={`note-swatch is-none ${!color ? 'is-sel' : ''}`} title={t('Nessun colore')} aria-label={t('Nessun colore')} aria-pressed={!color} onClick={() => setColor(null)}><span /></button>
                {COLORS.map((item) => (
                  <button
                    key={item.value}
                    className={`note-swatch ${color === item.value ? 'is-sel' : ''}`}
                    title={t(item.label)}
                    aria-label={t('Colore {c}', { c: t(item.label) })}
                    aria-pressed={color === item.value}
                    onClick={() => setColor(item.value)}
                  >
                    <span style={{ background: item.value }} />
                  </button>
                ))}
              </div>
            </div>
            <label className="note-editor__option note-editor__folder">
              <span className="note-editor__option-label">{t('Cartella')}</span>
              <span className="note-editor__select-wrap">
                <Icon name="folder" size={13} />
                <select className="input" value={folderId ?? ''} onChange={(e) => setFolderId(e.target.value || null)} aria-label={t('Cartella della nota')}>
                  <option value="">{t('Nessuna cartella')}</option>
                  {folders.map((f) => <option key={f.id} value={f.id}>{folderPath(f)}</option>)}
                </select>
              </span>
            </label>
          </div>

          <div className="note-editor__danger-zone">
            <span className="note-editor__status note-editor__status--mobile" role="status">{saveStatus}</span>
            <button className="btn btn--sm btn--danger" disabled={remove.isPending} onClick={() => void deleteCurrentNote()}>
              <Icon name="trash" size={12} /> {t('Elimina')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** One folder row in the tree — a drop target for notes and other folders. */
function FolderRow({ folder, depth, count, active, expanded, hasChildren, onToggle, onSelect, onDropNote, onDropFolder, onRename, onAddSub, onDelete, onDragStart }: {
  folder: NoteFolder; depth: number; count: number; active: boolean; expanded: boolean; hasChildren: boolean;
  onToggle: () => void; onSelect: () => void; onDropNote: (noteId: string) => void; onDropFolder: (folderId: string) => void;
  onRename: () => void; onAddSub: () => void; onDelete: () => void; onDragStart: () => void;
}) {
  const t = useT();
  const [over, setOver] = useState(false);
  return (
    <div className={`notes-folder ${active ? 'is-active' : ''} ${over ? 'is-drop' : ''}`} style={{ paddingLeft: 8 + depth * 14 }}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/folder', folder.id); e.dataTransfer.effectAllowed = 'move'; onDragStart(); }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false);
        const noteId = e.dataTransfer.getData('text/note');
        const folderId = e.dataTransfer.getData('text/folder');
        if (noteId) onDropNote(noteId);
        else if (folderId && folderId !== folder.id) onDropFolder(folderId);
      }}>
      {hasChildren
        ? <button className="notes-folder__caret" onClick={(e) => { e.stopPropagation(); onToggle(); }}><Icon name={expanded ? 'chevD' : 'chevR'} size={11} /></button>
        : <span className="notes-folder__caret" />}
      <button className="notes-folder__label" onClick={onSelect}>
        <Icon name="folder" size={13} /> <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{folder.name}</span> <span className="muted">{count}</span>
      </button>
      <button className="notes-folder__act" title={t('Nuova sotto-cartella')} onClick={onAddSub}><Icon name="plus" size={11} /></button>
      <button className="notes-folder__act" title={t('Rinomina')} onClick={onRename}><Icon name="edit" size={11} /></button>
      <button className="notes-folder__act" title={t('Elimina cartella')} onClick={onDelete}><Icon name="trash" size={11} /></button>
    </div>
  );
}

export function NotesScreen() {
  const t = useT();
  const confirm = useConfirm();
  const q = useNotes();
  const foldersQ = useNoteFolders();
  const create = useCreateNote();
  const updateNote = useUpdateNote();
  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();

  const [openId, setOpenId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('all');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Custom folder-name dialog (replaces window.prompt).
  const [folderDialog, setFolderDialog] = useState<null | { mode: 'create'; parentId: string | null } | { mode: 'rename'; id: string; name: string }>(null);

  const notes = q.data ?? [];
  const folders = foldersQ.data ?? [];

  // Notes count including nested sub-folders.
  const descendantIds = (fid: string): string[] => {
    const kids = folders.filter((f) => f.parentId === fid);
    return [fid, ...kids.flatMap((k) => descendantIds(k.id))];
  };
  const countIn = (fid: string) => { const ids = new Set(descendantIds(fid)); return notes.filter((n) => n.folderId && ids.has(n.folderId)).length; };

  const filtered = useMemo(() => {
    let list = notes;
    if (view === 'none') list = list.filter((n) => !n.folderId);
    else if (view === 'starred') list = list.filter((n) => n.pinned);
    else if (view !== 'all') { const ids = new Set(descendantIds(view)); list = list.filter((n) => n.folderId && ids.has(n.folderId)); }
    const term = search.trim().toLowerCase();
    if (term) list = list.filter((n) => (n.title + ' ' + n.body.replace(/<[^>]+>/g, ' ')).toLowerCase().includes(term));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, folders, view, search]);

  const open = notes.find((n) => n.id === openId) ?? null;
  const roots = folders.filter((f) => !f.parentId);

  async function addNote() {
    const folderId = view !== 'all' && view !== 'none' && view !== 'starred' ? view : undefined;
    const created = await create.mutateAsync({ title: '', body: '', ...(folderId ? { folderId } : {}) });
    setOpenId(created.id);
  }
  const moveNote = (noteId: string, folderId: string | null) => updateNote.mutate({ id: noteId, folderId });
  const moveFolder = (folderId: string, parentId: string | null) => updateFolder.mutate({ id: folderId, parentId });

  /** Reorder within the current view: drop dragged note before/after a target, renumber. */
  function reorderNote(draggedId: string, targetId: string, side: 'before' | 'after') {
    const list = filtered.filter((n) => n.id !== draggedId);
    const idx = list.findIndex((n) => n.id === targetId);
    if (idx < 0) return;
    const dragged = notes.find((n) => n.id === draggedId);
    if (!dragged) return;
    list.splice(side === 'before' ? idx : idx + 1, 0, dragged);
    // Persist a dense order; only PATCH the ones whose order actually changed.
    list.forEach((n, i) => { if (n.order !== i) updateNote.mutate({ id: n.id, order: i }); });
  }

  // Recursive folder tree renderer.
  const renderFolders = (parentId: string | null, depth: number): React.ReactNode =>
    folders.filter((f) => f.parentId === parentId).map((f) => {
      const kids = folders.filter((c) => c.parentId === f.id);
      const isOpen = expanded[f.id] ?? true;
      return (
        <div key={f.id}>
          <FolderRow folder={f} depth={depth} count={countIn(f.id)} active={view === f.id}
            expanded={isOpen} hasChildren={kids.length > 0}
            onToggle={() => setExpanded((x) => ({ ...x, [f.id]: !isOpen }))}
            onSelect={() => setView(f.id)}
            onDropNote={(nid) => moveNote(nid, f.id)}
            onDropFolder={(fid) => moveFolder(fid, f.id)}
            onDragStart={() => { /* dataTransfer set in row */ }}
            onRename={() => setFolderDialog({ mode: 'rename', id: f.id, name: f.name })}
            onAddSub={() => setFolderDialog({ mode: 'create', parentId: f.id })}
            onDelete={async () => { if (await confirm({ message: t('Eliminare “{name}” e le sue sotto-cartelle? Le note resteranno.', { name: f.name }), confirmLabel: t('Elimina'), danger: true })) { deleteFolder.mutate(f.id); if (view === f.id) setView('all'); } }} />
          {isOpen && renderFolders(f.id, depth + 1)}
        </div>
      );
    });

  const staticRow = (id: View, label: string, icon: 'list' | 'folder' | 'star', n: number, dropToNull = false) => (
    <div className={`notes-folder ${view === id ? 'is-active' : ''}`}
      onDragOver={dropToNull ? (e) => e.preventDefault() : undefined}
      onDrop={dropToNull ? (e) => { e.preventDefault(); const nid = e.dataTransfer.getData('text/note'); const fid = e.dataTransfer.getData('text/folder'); if (nid) moveNote(nid, null); else if (fid) moveFolder(fid, null); } : undefined}>
      <span className="notes-folder__caret" />
      <button className="notes-folder__label" onClick={() => setView(id)}>
        <Icon name={icon} size={13} /> <span style={{ flex: 1 }}>{label}</span> <span className="muted">{n}</span>
      </button>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="h1">{t('Appunti personali')}</h1>
          <div className="page-head__sub">{t('Note private. Trascina le note nelle cartelle, crea sotto-cartelle e formatta a piacere.')}</div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input className="input" placeholder={t('Cerca…')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
          <button className="btn btn--brand" onClick={() => void addNote()}><Icon name="plus" size={14} /> {t('Nuova nota')}</button>
        </div>
      </div>

      <div className="notes-screen-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 18, alignItems: 'start' }}>
        <div className="card" style={{ padding: 8 }}>
          {staticRow('all', t('Tutte'), 'list', notes.length)}
          {staticRow('starred', t('Preferiti'), 'star', notes.filter((n) => n.pinned).length)}
          {staticRow('none', t('Senza cartella'), 'folder', notes.filter((n) => !n.folderId).length, true)}
          <div className="notes-folder__sep" />
          {renderFolders(null, 0)}
          {roots.length === 0 && <div className="muted" style={{ fontSize: 12, padding: '4px 10px' }}>{t('Nessuna cartella.')}</div>}
          <button className="notes-folder notes-folder--add" onClick={() => setFolderDialog({ mode: 'create', parentId: null })}>
            <span className="notes-folder__caret" /><Icon name="plus" size={13} /> {t('Nuova cartella')}
          </button>
        </div>

        <Query query={q} empty={{ test: (d) => d.length === 0, title: t('Nessun appunto'), hint: t('Crea la tua prima nota privata con “Nuova nota”.') }}>
          {() => (
            filtered.length === 0
              ? <div className="muted" style={{ padding: 20 }}>{search ? t('Nessuna nota per “{q}”.', { q: search }) : t('Nessuna nota qui. Trascina una nota o creane una nuova.')}</div>
              : <div className="notes-grid">{filtered.map((n) => (
                  <NoteCard key={n.id} note={n} onOpen={() => setOpenId(n.id)}
                    onTogglePin={() => updateNote.mutate({ id: n.id, pinned: !n.pinned })}
                    onReorderDrop={(draggedId, side) => reorderNote(draggedId, n.id, side)} />
                ))}</div>
          )}
        </Query>
      </div>

      {open && <NoteEditor note={open} folders={folders} onClose={() => setOpenId(null)} />}

      {folderDialog && (
        <FolderNameModal
          title={folderDialog.mode === 'create' ? (folderDialog.parentId ? t('Nuova sotto-cartella') : t('Nuova cartella')) : t('Rinomina cartella')}
          initial={folderDialog.mode === 'rename' ? folderDialog.name : ''}
          onCancel={() => setFolderDialog(null)}
          onConfirm={(name) => {
            if (folderDialog.mode === 'create') createFolder.mutate({ name, parentId: folderDialog.parentId });
            else updateFolder.mutate({ id: folderDialog.id, name });
            setFolderDialog(null);
          }} />
      )}
    </div>
  );
}
